/**
 * The watch query's JSON → `PrSnapshot`s (wave D spec §2.3 "Mapping to `PrSnapshot`").
 *
 * Moved from the runner into core (decision 0069): the runner's watcher and the hub's GitHub App path normalize with
 * this one function. The app path sets `observedBy: "github_app"` and overrides the viewer login and the merge method
 * (the app's GraphQL viewer is the bot, whose login and default merge method say nothing about the PR's owner).
 *
 * Every string GitHub supplies is cut to its protocol cap here, and each snapshot is decoded with the protocol schema
 * before it leaves (anything that still does not fit is dropped and counted as an error, never sent half-valid).
 * Excerpts (review and comment bodies) are scrubbed (`scrubSecrets`), whitespace-collapsed and cut to 280 chars, and
 * only produced when `excerpts` is on (`[runner] prWatchExcerpts`).
 */
import { PrSnapshot } from "@minga/protocol"
import { Option, Schema } from "effect"
import { scrubSecrets } from "../activity"
import type { RepoSlot } from "./query"

type Snapshot = typeof PrSnapshot.Type
type Mergeable = Snapshot["mergeable"]
type MergeMethodT = NonNullable<Snapshot["mergeMethod"]>

/** What the watcher asked for one PR (the `WatchedPr` fields normalize needs). */
export interface AskedPr {
  readonly artifactId: string
  readonly url: string
  readonly repo: string
  readonly number: number
}

/** What a repository allows, for `mergeMethodFor`: its own pick (`mergeMethodOf`) and each method's flag. */
export interface RepoMergeMethods {
  /** `mergeMethodOf(repo)`: the viewer's default when allowed, else the first allowed. */
  readonly method: MergeMethodT | null
  readonly allowed: Readonly<Record<MergeMethodT, boolean>>
}

interface NormalizeBase {
  readonly now: number
  /** Send review/comment excerpts (`[runner] prWatchExcerpts`, default true). */
  readonly excerpts: boolean
  /** The last known (not `unknown`) mergeability of a PR, by artifact id (§2.4). */
  readonly knownMergeable?: (artifactId: string) => Mergeable | undefined
  /**
   * The snapshot's `viewerLogin` for one PR instead of the query's `viewer.login` (the GitHub App path: the PR owner's
   * gh login; null = unknown). Absent: the query's viewer.
   */
  readonly viewerLoginFor?: (asked: AskedPr) => string | null
  /** The snapshot's `mergeMethod` for one PR instead of the repo's own pick (the GitHub App path, see `keepMergeMethod`). */
  readonly mergeMethodFor?: (asked: AskedPr, fromRepo: RepoMergeMethods) => MergeMethodT | null
}

/**
 * `observedBy`: the reporting runner's id, or `github_app` (decision 0069). `runnerId` is the older spelling of the
 * same option (a runner id), kept so the runner's watcher and tests read as before.
 */
export type NormalizeOptions = NormalizeBase &
  (
    | { readonly observedBy: string; readonly runnerId?: never }
    | { readonly runnerId: string; readonly observedBy?: never }
  )

const observerOf = (o: NormalizeOptions): string => o.observedBy ?? o.runnerId ?? ""

export type PrOutcome =
  | { readonly _tag: "ok"; readonly artifactId: string; readonly snapshot: Snapshot }
  /** The PR alias was null (deleted, or no access to it): counts toward `gone`. */
  | { readonly _tag: "gone"; readonly artifactId: string }
  /** The whole repository alias was null: no access to the repo with this gh login. */
  | { readonly _tag: "no_access"; readonly artifactId: string; readonly repo: string }
  /** The alias was there but did not normalize into a valid snapshot. */
  | { readonly _tag: "invalid"; readonly artifactId: string; readonly why: string }

export interface NormalizedWatch {
  readonly viewer: string | null
  /** Each repo slot's `nameWithOwner` as GitHub spells it now (a rename shows here), null when the repo was null. */
  readonly repoNames: ReadonlyArray<{ readonly asked: string; readonly nameWithOwner: string | null }>
  readonly rate: { readonly remaining: number; readonly resetAt: number } | null
  readonly outcomes: ReadonlyArray<PrOutcome>
}

type Json = Record<string, unknown>
const obj = (v: unknown): Json | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Json) : null
const arr = (v: unknown): ReadonlyArray<unknown> => (Array.isArray(v) ? v : [])
const str = (v: unknown): string | null => (typeof v === "string" ? v : null)
const nodes = (v: unknown): ReadonlyArray<unknown> => arr(obj(v)?.nodes)
const millis = (v: unknown): number | null => {
  const s = str(v)
  if (!s) return null
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : null
}
const cut = (s: string, n: number): string => (s.length > n ? s.slice(0, n) : s)

const LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})(?:\[bot\])?$/
const SHA = /^[0-9a-f]{40}(?:[0-9a-f]{24})?$/
const login = (v: unknown): string | null => {
  const s = str(v)
  return s && LOGIN.test(s) ? s : null
}
const sha = (v: unknown): string | null => {
  const s = str(v)
  return s && SHA.test(s) ? s : null
}

const WRITERS = new Set(["OWNER", "MEMBER", "COLLABORATOR"])

/** First 280 chars of a body, scrubbed, whitespace collapsed; null when empty. */
export const excerptOf = (body: unknown): string | null => {
  const s = str(body)
  if (!s) return null
  const one = scrubSecrets(s).replace(/\s+/g, " ").trim()
  return one ? cut(one, 280) : null
}

const allowedOf = (repo: Json | null): Record<MergeMethodT, boolean> => ({
  squash: repo?.squashMergeAllowed === true,
  merge: repo?.mergeCommitAllowed === true,
  rebase: repo?.rebaseMergeAllowed === true,
})

/** `viewerDefaultMergeMethod` when allowed, else the first allowed of squash, merge, rebase, else null. */
export const mergeMethodOf = (repo: Json | null): MergeMethodT | null => {
  if (!repo) return null
  const allowed = allowedOf(repo)
  const def = str(repo.viewerDefaultMergeMethod)?.toLowerCase()
  if (def === "squash" || def === "merge" || def === "rebase") if (allowed[def]) return def
  for (const m of ["squash", "merge", "rebase"] as const) if (allowed[m]) return m
  return null
}

/**
 * Decision 0069 §6.4: the last method a runner reported for this PR while the repo still allows it, else the repo's
 * own pick. Keeps the material hash stable when the GitHub App path takes over from a runner.
 */
export const keepMergeMethod = (
  last: MergeMethodT | null | undefined,
  fromRepo: RepoMergeMethods,
): MergeMethodT | null => (last != null && fromRepo.allowed[last] ? last : fromRepo.method)

const FAILING_CONCLUSIONS = new Set([
  "FAILURE",
  "TIMED_OUT",
  "CANCELLED",
  "ACTION_REQUIRED",
  "STARTUP_FAILURE",
])
const REVIEW_STATES: Record<string, Snapshot["reviews"][number]["state"]> = {
  APPROVED: "approved",
  CHANGES_REQUESTED: "changes_requested",
  COMMENTED: "commented",
  DISMISSED: "dismissed",
}

const authorOf = (n: Json): { login: string; bot: boolean } | null => {
  const a = obj(n.author)
  const l = login(a?.login)
  if (!l) return null
  return { login: l, bot: a?.__typename === "Bot" || l.endsWith("[bot]") }
}

const checksOf = (commit: Json | null): Snapshot["checks"] => {
  const rollup = obj(commit?.statusCheckRollup)
  if (!rollup) return { state: null, failing: [], running: 0 }
  const s = str(rollup.state)?.toLowerCase()
  const state =
    s === "pending" || s === "success" || s === "failure" || s === "error" || s === "expected" ? s : null
  const failing: string[] = []
  let running = 0
  for (const raw of nodes(rollup.contexts)) {
    const c = obj(raw)
    if (!c) continue
    if (c.__typename === "CheckRun") {
      const name = str(c.name) ?? "check"
      if (FAILING_CONCLUSIONS.has(str(c.conclusion) ?? "")) failing.push(cut(name, 100))
      if (str(c.status) !== "COMPLETED") running++
    } else if (c.__typename === "StatusContext") {
      const name = str(c.context) ?? "status"
      const st = str(c.state)
      if (st === "FAILURE" || st === "ERROR") failing.push(cut(name, 100))
      if (st === "PENDING") running++
    }
  }
  return { state, failing: [...new Set(failing)].slice(0, 10), running }
}

const decodeSnapshot = Schema.decodeUnknownOption(PrSnapshot)

/** One PR alias → a snapshot (decoded, so valid by construction), or why not. */
export const snapshotOf = (
  pr: Json,
  repo: Json | null,
  asked: AskedPr,
  viewer: string | null,
  o: NormalizeOptions,
): { ok: true; snapshot: Snapshot } | { ok: false; why: string } => {
  const st = str(pr.state)
  const state = st === "OPEN" ? "open" : st === "MERGED" ? "merged" : st === "CLOSED" ? "closed" : null
  if (!state) return { ok: false, why: `unknown state ${String(st)}` }
  const headSha = sha(pr.headRefOid)
  if (!headSha) return { ok: false, why: "no head sha" }
  const m = str(pr.mergeable)?.toLowerCase()
  let mergeable: Mergeable = m === "mergeable" || m === "conflicting" ? m : "unknown"
  if (mergeable === "unknown") mergeable = o.knownMergeable?.(asked.artifactId) ?? "unknown"
  const ms = str(pr.mergeStateStatus)?.toLowerCase() ?? "unknown"
  const mergeState = (
    ["behind", "blocked", "clean", "dirty", "draft", "has_hooks", "unknown", "unstable"].includes(ms)
      ? ms
      : "unknown"
  ) as Snapshot["mergeState"]
  const rd = str(pr.reviewDecision)?.toLowerCase()
  const reviewDecision = (
    rd === "approved" || rd === "changes_requested" || rd === "review_required" ? rd : null
  ) as Snapshot["reviewDecision"]

  const reviews: Array<Snapshot["reviews"][number]> = []
  for (const raw of nodes(pr.latestReviews)) {
    const n = obj(raw)
    if (!n) continue
    const at = millis(n.submittedAt)
    const rs = REVIEW_STATES[str(n.state) ?? ""]
    const who = authorOf(n)
    if (at === null || !rs || !who) continue
    const ex = o.excerpts ? excerptOf(n.body) : null
    reviews.push({
      login: who.login,
      state: rs,
      writer: WRITERS.has(str(n.authorAssociation) ?? ""),
      bot: who.bot,
      at,
      url: cut(str(n.url) ?? "", 300),
      commitSha: sha(obj(n.commit)?.oid),
      ...(ex ? { excerpt: ex } : {}),
    })
    if (reviews.length >= 10) break
  }

  const cmts = obj(pr.comments)
  const latest: Array<Snapshot["comments"]["latest"][number]> = []
  for (const raw of nodes(cmts)) {
    const n = obj(raw)
    if (!n) continue
    const at = millis(n.createdAt)
    const who = authorOf(n)
    if (at === null || !who) continue
    const ex = o.excerpts ? excerptOf(n.body) : null
    latest.push({
      login: who.login,
      writer: WRITERS.has(str(n.authorAssociation) ?? ""),
      bot: who.bot,
      at,
      url: cut(str(n.url) ?? "", 300),
      ...(ex ? { excerpt: ex } : {}),
    })
  }
  const total = typeof cmts?.totalCount === "number" && cmts.totalCount >= 0 ? Math.floor(cmts.totalCount) : 0

  const commit = obj(obj(nodes(pr.commits)[0])?.commit)
  let headAuthor: string | null = null
  for (const raw of nodes(commit?.authors)) {
    const l = login(obj(obj(raw)?.user)?.login)
    if (l) {
      headAuthor = l
      break
    }
  }

  const autoMerge: Snapshot["autoMerge"] =
    pr.isInMergeQueue === true ? "in_queue" : obj(pr.autoMergeRequest) ? "armed" : "off"

  const candidate = {
    artifactId: asked.artifactId,
    url: asked.url,
    repo: asked.repo,
    number: asked.number,
    state,
    draft: pr.isDraft === true,
    headSha,
    headRef: cut(str(pr.headRefName) ?? "", 255),
    baseRef: cut(str(pr.baseRefName) ?? "", 255),
    mergeable,
    mergeState,
    reviewDecision,
    reviews,
    comments: { total, latest: latest.slice(-5) },
    checks: checksOf(commit),
    headAuthor,
    mergedBy: login(obj(pr.mergedBy)?.login),
    mergedAt: millis(pr.mergedAt),
    mergeSha: sha(obj(pr.mergeCommit)?.oid),
    closedAt: millis(pr.closedAt),
    autoMerge,
    mergeMethod: o.mergeMethodFor
      ? o.mergeMethodFor(asked, { method: mergeMethodOf(repo), allowed: allowedOf(repo) })
      : mergeMethodOf(repo),
    viewerLogin: o.viewerLoginFor ? login(o.viewerLoginFor(asked)) : viewer,
    updatedAt: millis(pr.updatedAt) ?? o.now,
    observedAt: o.now,
    observedBy: observerOf(o),
    ownRepo: false,
  }
  const decoded = decodeSnapshot(candidate)
  return Option.isSome(decoded)
    ? { ok: true, snapshot: decoded.value }
    : { ok: false, why: "the snapshot does not match the protocol" }
}

/**
 * The `data` object of one watch call → an outcome per asked PR. `slots` is the call's query shape; `asked` maps
 * `repo#number` (repo lower-cased) to the watch entries (an entry is asked once even if listed twice).
 */
export const normalizeWatch = (
  data: unknown,
  slots: ReadonlyArray<RepoSlot>,
  asked: ReadonlyArray<AskedPr>,
  o: NormalizeOptions,
): NormalizedWatch => {
  const d = obj(data) ?? {}
  const viewer = login(obj(d.viewer)?.login)
  const rl = obj(d.rateLimit)
  const remaining = typeof rl?.remaining === "number" ? rl.remaining : null
  const rate = remaining === null ? null : { remaining, resetAt: millis(rl?.resetAt) ?? o.now + 3_600_000 }
  const outcomes: PrOutcome[] = []
  const repoNames: Array<{ asked: string; nameWithOwner: string | null }> = []
  slots.forEach((slot, r) => {
    const repo = obj(d[`r${r}`])
    repoNames.push({ asked: slot.repo, nameWithOwner: repo ? str(repo.nameWithOwner) : null })
    for (const p of slot.prs) {
      const entries = asked.filter(
        (a) => a.repo.toLowerCase() === slot.repo.toLowerCase() && a.number === p.number,
      )
      for (const a of entries) {
        if (!repo) {
          outcomes.push({ _tag: "no_access", artifactId: a.artifactId, repo: slot.repo })
          continue
        }
        const pr = obj(repo[p.alias])
        if (!pr) {
          outcomes.push({ _tag: "gone", artifactId: a.artifactId })
          continue
        }
        const s = snapshotOf(pr, repo, a, viewer, o)
        outcomes.push(
          s.ok
            ? { _tag: "ok", artifactId: a.artifactId, snapshot: s.snapshot }
            : { _tag: "invalid", artifactId: a.artifactId, why: s.why },
        )
      }
    }
  })
  return { viewer, repoNames, rate, outcomes }
}
